import { action, computed, makeObservable, observable } from 'mobx'
import { trackedAction } from 'mobx-async'

import { Alert } from '@avantstay/backoffice-core'

import { storeHolder } from '@new/__global/components/FastTapeChart/stores/TapeChartStore'
import {
  CreateBlockView,
  TapeBlockView,
} from '@new/domains/bookingsTapeChart/BookingsTapeChart.types'
import { CreateBlockFormProps } from '@new/domains/bookingsTapeChart/components/CreateBlockForm/CreateBlockForm.types'
// eslint-disable-next-line import/no-cycle
import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'
// eslint-disable-next-line import/no-cycle
import { fastBookingsTapeChartStore } from '@new/domains/fastBookingsTapeChart/stores/FastBookingsTapeChartStore'
import { DEFAULT_HOTEL_ID } from '@new/domains/hotelTapeChart/config/contants'
import { HotelFilterIds } from '@new/domains/hotelTapeChart/config/filtersConfig'

import { trackError, trackEvent } from '@/tracking/customEventReport'
import { arriere } from '@arriere/index'
import { CalendarBlocksProps, EditCalendarBlockProps } from '@arriere/types/CalendarBlocks.types'

class _TapeChartBlockStore {
  toggleCreateTapeBlockModal?: boolean

  createBlockTab = CreateBlockView.reservation

  createTapeBlockObject?: Partial<CreateBlockFormProps>

  selectedBlockObject?: TapeBlockView

  constructor() {
    makeObservable(this, {
      toggleCreateTapeBlockModal: observable,
      createBlockTab: observable,
      createTapeBlockObject: observable,
      selectedBlockObject: observable,
      hotelId: computed,
      blockHomeInfo: computed,
      setTapeBlockObject: action.bound,
      setCreateBlockTab: action.bound,
      setSelectedBlock: action.bound,
    })

    this.getBookingsList = trackedAction<any>(this.getBookingsList.bind(this))
    this.cancelTapeChartBlock = trackedAction<any>(this.cancelTapeChartBlock.bind(this))
    this.editTapeChartBlock = trackedAction<any>(this.editTapeChartBlock.bind(this))
    this.createTapeChartBlock = trackedAction<any>(this.createTapeChartBlock.bind(this))
  }

  get hotelId() {
    return (
      storeHolder.store?.filterStore?.filteringArgs?.[HotelFilterIds.HotelId] ?? DEFAULT_HOTEL_ID
    )
  }

  get blockHomeInfo() {
    const homeId = this.selectedBlockObject?.homeId

    if (homeId) {
      const homesSource =
        BookingsTapeChartStore.cacheObject.homes[0]?.data ?? fastBookingsTapeChartStore.homes
      return Promise.resolve(homesSource?.find(home => home.id.includes(homeId)))
    }

    return Promise.resolve(undefined)
  }

  setTapeBlockObject = (tapeBlock: Partial<CreateBlockFormProps>) => {
    this.createTapeBlockObject = tapeBlock
  }

  setCreateBlockTab = (view: CreateBlockView) => {
    this.createBlockTab = view
  }

  setSelectedBlock = (block?: TapeBlockView) => {
    this.selectedBlockObject = block
  }

  async createTapeChartBlock({
    bookingId,
    homeId,
    period,
    kind,
    note,
    expiresAt,
    notificationSetting,
    onSuccess,
  }: CalendarBlocksProps & { onSuccess?: VoidFunction }): Promise<void> {
    return arriere.calendarBlocks
      .createTapeChartBlock({
        bookingId,
        homeId,
        period,
        kind,
        note,
        expiresAt,
        notificationSetting,
      })
      .then(({ data, errors }) => {
        if (errors?.length) {
          throw new Error(errors[errors?.length - 1]?.message)
        }

        if (data) {
          trackEvent('Create block: Block created successfully!', {
            homeId,
            period,
            kind,
          })
          onSuccess?.()
          Alert.Success('Block created successfully.')
        }
      })
      .catch(error => {
        trackError('Create Block: error while trying to create block', {
          error,
          homeId,
          period,
          kind,
        })
        Alert.Error(`Error creating block: ${error}`)
      })
  }

  async editTapeChartBlock({
    id,
    newKind,
    newNote,
    newStart,
    newEnd,
    newExpiresAt,
    newNotificationSetting,
    onSuccess,
  }: EditCalendarBlockProps & { onSuccess?: VoidFunction }): Promise<void> {
    return arriere.calendarBlocks
      .editTapeChartBlock({
        id,
        newKind,
        newNote,
        newStart,
        newEnd,
        newExpiresAt,
        newNotificationSetting,
      })
      .then(({ data, errors }) => {
        if (errors?.length) {
          throw new Error(errors[errors?.length - 1]?.message)
        }

        if (data) {
          trackEvent('Update block: Block updated successfully!', {
            id,
          })
          onSuccess?.()
          Alert.Success('Block updated successfully.')
          fastBookingsTapeChartStore.reloadBlockedPeriods()
          storeHolder.store.apiStore?.reloadBlockedPeriods?.()
          return
        } else {
          throw new Error('Edit block failed')
        }
      })
      .catch(error => {
        if (error) {
          trackError('Update Block: error while trying to update block', {
            error,
            id,
            newKind,
            newNote,
            newStart,
            newEnd,
          })
          Alert.Error(`Error updating block: ${error}`)
        }
      })
  }

  async cancelTapeChartBlock(blockId: string): Promise<void> {
    return arriere.calendarBlocks
      .cancelTapeChartBlock(blockId)
      .then(({ data, errors }) => {
        if (errors?.length) {
          throw new Error(errors[errors?.length - 1]?.message)
        }

        if (data) {
          trackEvent('Cancel block: Block cancelled successfully!', {
            blockId,
          })
          Alert.Success('Block cancelled successfully.')
          fastBookingsTapeChartStore.reloadBlockedPeriods()
          storeHolder.store.apiStore?.reloadBlockedPeriods?.()
          return
        } else {
          throw new Error('Cancel block failed')
        }
      })
      .catch(error => {
        trackError('Cancel Block: error while trying to cancel block', {
          error,
          blockId,
        })
        Alert.Error(`Error cancelling block: ${error}`)
      })
  }

  async getBookingsList(query: string) {
    return arriere.calendarBlocks.getBookingsForTapechartBlocks(query)
  }
}

export const TapeChartBlockStore = new _TapeChartBlockStore()
